import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { GeoJsonObject, Feature, Geometry } from 'geojson';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import YearSelector from '@/components/YearSelector';
import CountySelector from '@/components/CountySelector';
import { getAllMunicipalities, Municipality, getFeesForYear, filterFees } from '@/lib/data';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';
import { getLatestYear } from '@/config/years';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const GEOJSON_URL = 'https://raw.githubusercontent.com/robhop/fylker-og-kommuner/main/Kommuner-S.geojson';

const NORWAY_CENTER: [number, number] = [64.8, 12.0];
const NORWAY_ZOOM = 5;
const NORWAY_BOUNDS: L.LatLngBoundsExpression = [[57.9, 4.6], [71.2, 31.1]];

// Beregn bounds fra GeoJSON-features basert på kommunenummer-liste
const getBoundsForKommuneIds = (
  geoJson: GeoJsonObject,
  kommuneIds: Set<string>
): L.LatLngBounds | null => {
  if (!('features' in geoJson)) return null;
  const bounds = L.latLngBounds([] as any);
  let hasPoints = false;
  const addPoint = (lon: number, lat: number) => {
    bounds.extend([lat, lon]);
    hasPoints = true;
  };
  const processCoords = (coords: any): void => {
    if (typeof coords[0] === 'number') {
      addPoint(coords[0], coords[1]);
    } else {
      coords.forEach(processCoords);
    }
  };
  geoJson.features.forEach((f: any) => {
    const nr = f.properties?.kommunenummer ?? f.properties?.id ?? '';
    if (!kommuneIds.has(nr)) return;
    const geom = f.geometry;
    if (geom?.coordinates) processCoords(geom.coordinates);
  });
  return hasPoints ? bounds : null;
};

const ChangeView: React.FC<{ bounds: L.LatLngBoundsExpression | null }> = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }, [bounds, map]);
  return null;
};

// Fargegradient: grønn (lav) -> gul -> rød (høy)
const getColor = (value: number, min: number, max: number): string => {
  if (max <= min) return '#94a3b8';
  const t = (value - min) / (max - min);
  if (t < 0.5) {
    const g = Math.round(200 + 55 * (1 - t * 2));
    const r = Math.round(34 + 221 * t * 2);
    return `rgb(${r}, ${g}, 47)`;
  } else {
    const r = 255;
    const g = Math.round(255 - 155 * (t - 0.5) * 2);
    const b = Math.round(47 - 47 * (t - 0.5) * 2);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const { enabledCategories } = useFeeCategories();
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [geoJson, setGeoJson] = useState<GeoJsonObject | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(() => getLatestYear());
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [dialogMunicipality, setDialogMunicipality] = useState<Municipality | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [munis, geo] = await Promise.all([
          getAllMunicipalities(),
          fetch(GEOJSON_URL).then(r => r.json())
        ]);
        setMunicipalities(munis);
        setGeoJson(geo);
      } catch (e) {
        console.error('Feil ved lasting av kartdata:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const feeByKommunenummer = React.useMemo(() => {
    const map = new Map<string, number>();
    municipalities.forEach(m => {
      const fees = filterFees(getFeesForYear(m, selectedYear), enabledCategories);
      const total = fees.reduce((sum, f) => sum + f.amount, 0);
      map.set(m.id, total);
    });
    return map;
  }, [municipalities, selectedYear, enabledCategories]);

  const counties = React.useMemo(() => {
    const set = new Set(municipalities.map(m => m.county).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [municipalities]);

  const countyBounds = React.useMemo((): L.LatLngBounds | null => {
    if (!selectedCounty || !geoJson) return null;
    const kommuneIds = new Set(
      municipalities.filter(m => m.county === selectedCounty).map(m => m.id)
    );
    return getBoundsForKommuneIds(geoJson, kommuneIds);
  }, [selectedCounty, geoJson, municipalities]);

  const activeKommunenummer = React.useMemo(() => {
    if (!selectedCounty) return null;
    return new Set(
      municipalities.filter(m => m.county === selectedCounty).map(m => m.id)
    );
  }, [selectedCounty, municipalities]);

  const { minFee, maxFee } = React.useMemo(() => {
    let fees: number[];
    if (activeKommunenummer) {
      fees = Array.from(feeByKommunenummer.entries())
        .filter(([id]) => activeKommunenummer.has(id))
        .map(([, v]) => v)
        .filter(v => v > 0);
    } else {
      fees = Array.from(feeByKommunenummer.values()).filter(v => v > 0);
    }
    if (fees.length === 0) return { minFee: 0, maxFee: 1 };
    return {
      minFee: Math.min(...fees),
      maxFee: Math.max(...fees)
    };
  }, [feeByKommunenummer, activeKommunenummer]);

  const style = useCallback((feature: GeoJsonObject) => {
    const f = feature as Feature<Geometry, { kommunenummer?: string }>;
    const props = f?.properties;
    const kommuneNr = props?.kommunenummer ?? (props as any)?.id ?? '';
    const isActive = !activeKommunenummer || activeKommunenummer.has(kommuneNr);

    if (!isActive) {
      return {
        fillColor: '#e2e8f0',
        color: '#cbd5e1',
        weight: 1,
        opacity: 0.5,
        fillOpacity: 0.25
      };
    }

    const totalFee = feeByKommunenummer.get(kommuneNr) ?? 0;
    const color = totalFee > 0 ? getColor(totalFee, minFee, maxFee) : '#e2e8f0';
    return {
      fillColor: color,
      color: '#64748b',
      weight: 1,
      opacity: 0.8,
      fillOpacity: 0.65
    };
  }, [feeByKommunenummer, minFee, maxFee, activeKommunenummer]);

  const onEachFeature = useCallback((feature: Feature<Geometry, { kommunenummer?: string; kommunenavn?: string }>, layer: any) => {
    const kommuneNr = feature.properties?.kommunenummer ?? feature.properties?.id ?? '';
    const navn = feature.properties?.kommunenavn ?? feature.properties?.name ?? 'Ukjent';
    const isActive = !activeKommunenummer || activeKommunenummer.has(kommuneNr);
    const totalFee = feeByKommunenummer.get(kommuneNr);
    const muni = municipalities.find(m => m.id === kommuneNr);
    const displayFee = totalFee != null && totalFee > 0
      ? totalFee.toLocaleString('nb-NO') + ' kr/år'
      : 'Ingen data';
    layer.bindTooltip(`<strong>${navn}</strong><br/>${displayFee}${!isActive ? '<br/><em>Ikke i valgt fylke</em>' : ''}`, {
      permanent: false,
      direction: 'top',
      className: 'map-tooltip'
    });
    layer.on({
      click: () => {
        if (muni && isActive) setDialogMunicipality(muni);
      },
      mouseover: (e: any) => {
        if (isActive) {
          e.target.setStyle({ weight: 2, fillOpacity: 0.85 });
        }
        e.target.bringToFront();
      },
      mouseout: (e: any) => {
        const s = style(feature as GeoJsonObject);
        e.target.setStyle(s);
      }
    });
    (layer as any).feature = feature;
  }, [feeByKommunenummer, municipalities, style, activeKommunenummer]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-softWhite">
        <Seo title="Kart | Kommunale avgifter i Norge" description="Se kommunale avgifter på kart. Klikk på en kommune for detaljer. Farger viser nivå på totale avgifter." path="/kart" />
        <Header />
        <main className="flex-grow flex flex-col">
          <div className="container mx-auto px-4 md:px-6 py-8 flex flex-col w-full">
            <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl border border-nordicBlue/10 bg-gradient-to-br from-nordicBlue/5 to-transparent">
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-7 w-32 mb-2" />
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
            <Skeleton className="h-[70vh] min-h-[400px] w-full rounded-lg" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      <Seo title="Kart | Kommunale avgifter i Norge" description="Se kommunale avgifter på kart. Klikk på en kommune for detaljer. Farger viser nivå på totale avgifter." path="/kart" />
      <Header />
      <main className="flex-grow flex flex-col">
        <div className="container mx-auto px-4 md:px-6 py-8 flex flex-col w-full">
          {/* Hero – samme kortstil som forsiden */}
          <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
            <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
              <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" />
              Forside
            </Link>
              <span>/</span>
              <span className="text-nordicBlue font-semibold">Kart</span>
            </nav>
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">Kartvisning</h1>
                <p className="text-sm text-warmGray">
                  Klikk på en kommune for å se info. Farger viser nivå på totale avgifter (grønn = lavest, rød = høyest).
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <CountySelector
                  municipalities={municipalities}
                  onCountySelect={setSelectedCounty}
                  countyList={counties}
                  value={selectedCounty}
                  selectContentClassName="z-[1000]"
                />
                <YearSelector value={selectedYear} onChange={setSelectedYear} selectContentClassName="z-[1000]" />
              </div>
            </div>
          </div>
          <div className="relative h-[70vh] min-h-[400px] rounded-lg overflow-hidden border border-slate-100 shadow-sm">
          <MapContainer
            center={NORWAY_CENTER}
            zoom={NORWAY_ZOOM}
            bounds={NORWAY_BOUNDS}
            boundsOptions={{ padding: [20, 20], maxZoom: 12 }}
            className="!h-full !w-full"
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoJson && (
              <GeoJSON
                key={`${selectedYear}-${selectedCounty ?? 'alle'}`}
                data={geoJson}
                style={style}
                onEachFeature={onEachFeature}
              />
            )}
            <ChangeView bounds={selectedCounty ? (countyBounds ?? NORWAY_BOUNDS) : NORWAY_BOUNDS} />
          </MapContainer>
          <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 flex flex-wrap items-center gap-2 sm:gap-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 shadow-sm border border-slate-100">
            <span className="text-xs font-medium text-warmGray">Avgiftsnivå:</span>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xs">Lav</span>
              <div
                className="flex-1 h-3 rounded-full max-w-48"
                style={{
                  background: `linear-gradient(to right, ${getColor(minFee, minFee, maxFee)}, ${getColor(maxFee, minFee, maxFee)})`
                }}
              />
              <span className="text-xs">Høy</span>
            </div>
          </div>
        </div>
        </div>

        <Dialog open={!!dialogMunicipality} onOpenChange={(open) => !open && setDialogMunicipality(null)}>
          <DialogContent className="z-[1000] max-w-sm" overlayClassName="z-[1000]">
            <DialogHeader>
              <DialogTitle>{dialogMunicipality?.name}</DialogTitle>
            </DialogHeader>
            {dialogMunicipality && (
              <div className="space-y-4">
                <p className="text-sm text-warmGray">{dialogMunicipality.county}</p>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Avgifter for {selectedYear}:</p>
                  <ul className="space-y-1 text-sm">
                    {filterFees(getFeesForYear(dialogMunicipality, selectedYear), enabledCategories).map((fee) => (
                      <li key={fee.id} className="flex justify-between">
                        <span>{fee.name}:</span>
                        <span>{fee.amount.toLocaleString('nb-NO')} kr/år</span>
                      </li>
                    ))}
                  </ul>
                  <p className="pt-2 font-medium border-t">
                    Totalt: {filterFees(getFeesForYear(dialogMunicipality, selectedYear), enabledCategories).reduce((s, f) => s + f.amount, 0).toLocaleString('nb-NO')} kr/år
                  </p>
                </div>
                <Button
                  className="w-full"
                  onClick={() => navigate(`/municipality/${dialogMunicipality.id}`)}
                >
                  Se full detaljer
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default MapPage;
